import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Camera, Video, FileText, Trash2, Save, ChevronLeft, ChevronRight, Play, Image as ImageIcon, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkerDailyLog = () => {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [description, setDescription] = useState('');
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewingMedia, setViewingMedia] = useState(null);
  const [dirty, setDirty] = useState(false);
  const fileInputRef = useRef(null);

  const fetchLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/worker/work-logs`, {
        params: { date: selectedDate }, withCredentials: true
      });
      if (res.data && res.data.length > 0) {
        setDescription(res.data[0].description || '');
        setMedia(res.data[0].media || []);
      } else {
        setDescription('');
        setMedia([]);
      }
      setDirty(false);
    } catch (e) { /* silenced */ }
  }, [selectedDate]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/worker/work-logs`, {
        date: selectedDate, description
      }, { withCredentials: true });
      setDirty(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    for (const file of files) {
      try {
        const b64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.readAsDataURL(file);
        });

        const isVideo = file.type.startsWith('video/');
        await axios.post(`${API_URL}/api/worker/work-logs/media`, {
          date: selectedDate,
          file_base64: b64,
          filename: file.name,
          media_type: isVideo ? 'video' : 'image',
        }, { withCredentials: true });
      } catch (err) {
        alert(`Errore upload ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    fetchLog();
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Eliminare questo file?')) return;
    try {
      await axios.delete(`${API_URL}/api/worker/work-logs/media/${mediaId}`, { withCredentials: true });
      fetchLog();
    } catch (e) { alert('Errore eliminazione'); }
  };

  const viewMedia = async (item) => {
    try {
      const res = await axios.get(`${API_URL}/api/files/${item.path}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      setViewingMedia({ ...item, url });
    } catch (e) { alert('Errore caricamento file'); }
  };

  const shiftDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const formatDate = (d) => {
    const dt = new Date(d + 'T12:00:00');
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return `${days[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
  };

  return (
    <>
      <Card className="border-[#E4E4E7] shadow-sm" data-testid="worker-daily-log-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-[#09090B] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#8B5CF6]" />
            Diario Lavorazioni
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Date selector */}
          <div className="flex items-center justify-between bg-[#F4F4F5] rounded-lg p-2">
            <button onClick={() => shiftDate(-1)} className="p-1 hover:bg-white rounded" data-testid="log-prev-day">
              <ChevronLeft className="w-5 h-5 text-[#71717A]" />
            </button>
            <div className="text-center">
              <p className="text-sm font-semibold text-[#09090B]">{formatDate(selectedDate)}</p>
              {selectedDate === today && (
                <p className="text-[10px] text-[#8B5CF6] font-medium">OGGI</p>
              )}
            </div>
            <button onClick={() => shiftDate(1)} className="p-1 hover:bg-white rounded" data-testid="log-next-day"
              disabled={selectedDate >= today}>
              <ChevronRight className={`w-5 h-5 ${selectedDate >= today ? 'text-[#E4E4E7]' : 'text-[#71717A]'}`} />
            </button>
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#09090B]">Descrizione lavorazione</label>
            <textarea
              value={description}
              onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
              placeholder="Descrivi il lavoro svolto oggi..."
              data-testid="log-description-input"
              className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
            />
          </div>

          {/* Save + Upload buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || !dirty}
              data-testid="log-save-btn"
              className={`flex-1 ${dirty ? 'bg-[#8B5CF6] hover:bg-[#7C3AED]' : 'bg-[#8B5CF6]/40'}`}
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Salvo...' : 'Salva'}
            </Button>

            <label className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                data-testid="log-file-input"
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                data-testid="log-upload-btn"
                className="w-full border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/5"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Carico...</>
                ) : (
                  <><Camera className="w-4 h-4 mr-1" />Foto / Video</>
                )}
              </Button>
            </label>
          </div>

          {/* Media gallery */}
          {media.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#71717A]">{media.length} file allegati</p>
              <div className="grid grid-cols-3 gap-2">
                {media.map((item) => (
                  <div key={item.media_id} className="relative group rounded-lg overflow-hidden border border-[#E4E4E7] aspect-square bg-[#F4F4F5]"
                    data-testid={`media-item-${item.media_id}`}>
                    <button
                      onClick={() => viewMedia(item)}
                      className="w-full h-full flex items-center justify-center"
                    >
                      {item.type === 'video' ? (
                        <div className="flex flex-col items-center gap-1">
                          <Play className="w-8 h-8 text-[#8B5CF6]" />
                          <span className="text-[10px] text-[#71717A] truncate max-w-full px-1">{item.original_filename}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <ImageIcon className="w-8 h-8 text-[#8B5CF6]" />
                          <span className="text-[10px] text-[#71717A] truncate max-w-full px-1">{item.original_filename}</span>
                        </div>
                      )}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteMedia(item.media_id); }}
                      className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-media-${item.media_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-[#FF3B30]" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Media viewer modal */}
      {viewingMedia && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => { if (viewingMedia.url) URL.revokeObjectURL(viewingMedia.url); setViewingMedia(null); }}>
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-2">
              {viewingMedia.type === 'video' ? (
                <video src={viewingMedia.url} controls className="w-full rounded" data-testid="media-viewer-video" />
              ) : (
                <img src={viewingMedia.url} alt={viewingMedia.original_filename} className="w-full rounded" data-testid="media-viewer-image" />
              )}
              <p className="text-xs text-[#71717A] mt-1 text-center">{viewingMedia.original_filename}</p>
              <Button variant="outline" className="w-full mt-2"
                onClick={() => { if (viewingMedia.url) URL.revokeObjectURL(viewingMedia.url); setViewingMedia(null); }}>
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkerDailyLog;
